import { normalizeLocale, type SupportedLocale } from './config';

/**
 * Localized copy for backend errors that name the organization. The backend
 * keeps "organization" in its codes and messages; the UI always says
 * "Workspace". `src/lib/workspace-error-message.ts` maps codes and messages
 * to these ids.
 */
export type WorkspaceErrorId =
  | 'notFound'
  | 'notFoundOrDenied'
  | 'accessDenied'
  | 'notMember'
  | 'adminRequired'
  | 'slugUnavailable'
  | 'slugImmutable'
  | 'deletionInProgress'
  | 'workspaceRequired'
  | 'otherWorkspace'
  | 'memberNotInWorkspace'
  | 'userNotMember';

type WorkspaceErrorCopy = Record<WorkspaceErrorId, string>;

const EN: WorkspaceErrorCopy = {
  notFound: 'Workspace not found.',
  notFoundOrDenied:
    "This workspace doesn't exist or you don't have access to it.",
  accessDenied: "You don't have access to this workspace.",
  notMember: "You're not a member of this workspace.",
  adminRequired: 'Only workspace admins can do this.',
  slugUnavailable:
    'That workspace URL is already taken. Please choose another.',
  slugImmutable: "The workspace URL can't be changed.",
  deletionInProgress: 'This workspace is being deleted.',
  workspaceRequired: 'Open a workspace to continue.',
  otherWorkspace: 'This item belongs to a different workspace.',
  memberNotInWorkspace: 'Everyone you add must be a member of this workspace.',
  userNotMember: "This user isn't a member of the workspace.",
};

const COPY: Record<SupportedLocale, WorkspaceErrorCopy> = {
  en: EN,
  ar: {
    notFound: 'تعذر العثور على مساحة العمل.',
    notFoundOrDenied:
      'مساحة العمل هذه غير موجودة أو ليس لديك صلاحية الوصول إليها.',
    accessDenied: 'ليس لديك صلاحية الوصول إلى مساحة العمل هذه.',
    notMember: 'أنت لست عضوًا في مساحة العمل هذه.',
    adminRequired: 'يمكن لمشرفي مساحة العمل فقط تنفيذ هذا الإجراء.',
    slugUnavailable:
      'عنوان URL لمساحة العمل هذا مستخدم بالفعل. يُرجى اختيار عنوان آخر.',
    slugImmutable: 'لا يمكن تغيير عنوان URL لمساحة العمل.',
    deletionInProgress: 'يجري حذف مساحة العمل هذه.',
    workspaceRequired: 'افتح مساحة عمل للمتابعة.',
    otherWorkspace: 'ينتمي هذا العنصر إلى مساحة عمل أخرى.',
    memberNotInWorkspace: 'يجب أن يكون كل من تضيفه عضوًا في مساحة العمل هذه.',
    userNotMember: 'هذا المستخدم ليس عضوًا في مساحة العمل.',
  },
  de: {
    notFound: 'Workspace nicht gefunden.',
    notFoundOrDenied:
      'Dieser Workspace existiert nicht oder du hast keinen Zugriff darauf.',
    accessDenied: 'Du hast keinen Zugriff auf diesen Workspace.',
    notMember: 'Du bist kein Mitglied dieses Workspace.',
    adminRequired: 'Nur Workspace-Admins können das tun.',
    slugUnavailable:
      'Diese Workspace-URL ist bereits vergeben. Bitte wähle eine andere.',
    slugImmutable: 'Die Workspace-URL kann nicht geändert werden.',
    deletionInProgress: 'Dieser Workspace wird gerade gelöscht.',
    workspaceRequired: 'Öffne einen Workspace, um fortzufahren.',
    otherWorkspace: 'Dieses Element gehört zu einem anderen Workspace.',
    memberNotInWorkspace:
      'Alle, die du hinzufügst, müssen Mitglied dieses Workspace sein.',
    userNotMember: 'Diese Person ist kein Mitglied des Workspace.',
  },
  es: {
    notFound: 'No se encontró el espacio de trabajo.',
    notFoundOrDenied:
      'Este espacio de trabajo no existe o no tienes acceso a él.',
    accessDenied: 'No tienes acceso a este espacio de trabajo.',
    notMember: 'No eres miembro de este espacio de trabajo.',
    adminRequired:
      'Solo los administradores del espacio de trabajo pueden hacer esto.',
    slugUnavailable:
      'Esa URL del espacio de trabajo ya está en uso. Elige otra.',
    slugImmutable: 'La URL del espacio de trabajo no se puede cambiar.',
    deletionInProgress: 'Este espacio de trabajo se está eliminando.',
    workspaceRequired: 'Abre un espacio de trabajo para continuar.',
    otherWorkspace: 'Este elemento pertenece a otro espacio de trabajo.',
    memberNotInWorkspace:
      'Todas las personas que añadas deben ser miembros de este espacio de trabajo.',
    userNotMember: 'Este usuario no es miembro del espacio de trabajo.',
  },
  fr: {
    notFound: 'Espace de travail introuvable.',
    notFoundOrDenied:
      'Cet espace de travail n’existe pas ou vous n’y avez pas accès.',
    accessDenied: 'Vous n’avez pas accès à cet espace de travail.',
    notMember: 'Vous n’êtes pas membre de cet espace de travail.',
    adminRequired:
      'Seuls les administrateurs de l’espace de travail peuvent effectuer cette action.',
    slugUnavailable:
      'Cette URL d’espace de travail est déjà utilisée. Veuillez en choisir une autre.',
    slugImmutable: 'L’URL de l’espace de travail ne peut pas être modifiée.',
    deletionInProgress: 'Cet espace de travail est en cours de suppression.',
    workspaceRequired: 'Ouvrez un espace de travail pour continuer.',
    otherWorkspace: 'Cet élément appartient à un autre espace de travail.',
    memberNotInWorkspace:
      'Toutes les personnes ajoutées doivent être membres de cet espace de travail.',
    userNotMember: 'Cet utilisateur n’est pas membre de l’espace de travail.',
  },
  hi: {
    notFound: 'वर्कस्पेस नहीं मिला।',
    notFoundOrDenied:
      'यह वर्कस्पेस मौजूद नहीं है या आपके पास इसकी पहुँच नहीं है।',
    accessDenied: 'आपके पास इस वर्कस्पेस की पहुँच नहीं है।',
    notMember: 'आप इस वर्कस्पेस के सदस्य नहीं हैं।',
    adminRequired: 'केवल वर्कस्पेस एडमिन ही यह कर सकते हैं।',
    slugUnavailable:
      'यह वर्कस्पेस URL पहले से लिया जा चुका है। कृपया कोई दूसरा चुनें।',
    slugImmutable: 'वर्कस्पेस URL बदला नहीं जा सकता।',
    deletionInProgress: 'यह वर्कस्पेस हटाया जा रहा है।',
    workspaceRequired: 'जारी रखने के लिए कोई वर्कस्पेस खोलें।',
    otherWorkspace: 'यह आइटम किसी दूसरे वर्कस्पेस का है।',
    memberNotInWorkspace:
      'आप जिन्हें जोड़ते हैं, उन्हें इस वर्कस्पेस का सदस्य होना चाहिए।',
    userNotMember: 'यह उपयोगकर्ता वर्कस्पेस का सदस्य नहीं है।',
  },
  id: {
    notFound: 'Ruang kerja tidak ditemukan.',
    notFoundOrDenied:
      'Ruang kerja ini tidak ada atau Anda tidak memiliki akses ke sana.',
    accessDenied: 'Anda tidak memiliki akses ke ruang kerja ini.',
    notMember: 'Anda bukan anggota ruang kerja ini.',
    adminRequired: 'Hanya admin ruang kerja yang dapat melakukan ini.',
    slugUnavailable:
      'URL ruang kerja tersebut sudah digunakan. Silakan pilih yang lain.',
    slugImmutable: 'URL ruang kerja tidak dapat diubah.',
    deletionInProgress: 'Ruang kerja ini sedang dihapus.',
    workspaceRequired: 'Buka ruang kerja untuk melanjutkan.',
    otherWorkspace: 'Item ini milik ruang kerja lain.',
    memberNotInWorkspace:
      'Semua orang yang Anda tambahkan harus menjadi anggota ruang kerja ini.',
    userNotMember: 'Pengguna ini bukan anggota ruang kerja.',
  },
  it: {
    notFound: 'Workspace non trovato.',
    notFoundOrDenied: 'Questo workspace non esiste o non hai accesso.',
    accessDenied: 'Non hai accesso a questo workspace.',
    notMember: 'Non sei membro di questo workspace.',
    adminRequired: 'Solo gli amministratori del workspace possono farlo.',
    slugUnavailable:
      'Questo URL del workspace è già in uso. Scegline un altro.',
    slugImmutable: 'L’URL del workspace non può essere modificato.',
    deletionInProgress: 'Questo workspace è in fase di eliminazione.',
    workspaceRequired: 'Apri un workspace per continuare.',
    otherWorkspace: 'Questo elemento appartiene a un altro workspace.',
    memberNotInWorkspace:
      'Tutte le persone che aggiungi devono essere membri di questo workspace.',
    userNotMember: 'Questo utente non è membro del workspace.',
  },
  ja: {
    notFound: 'ワークスペースが見つかりません。',
    notFoundOrDenied:
      'このワークスペースは存在しないか、アクセス権がありません。',
    accessDenied: 'このワークスペースへのアクセス権がありません。',
    notMember: 'このワークスペースのメンバーではありません。',
    adminRequired: 'この操作はワークスペースの管理者のみ実行できます。',
    slugUnavailable:
      'そのワークスペースURLはすでに使われています。別のURLを選んでください。',
    slugImmutable: 'ワークスペースURLは変更できません。',
    deletionInProgress: 'このワークスペースは削除中です。',
    workspaceRequired: '続行するにはワークスペースを開いてください。',
    otherWorkspace: 'この項目は別のワークスペースに属しています。',
    memberNotInWorkspace:
      '追加する人は全員このワークスペースのメンバーである必要があります。',
    userNotMember: 'このユーザーはワークスペースのメンバーではありません。',
  },
  ko: {
    notFound: '워크스페이스를 찾을 수 없습니다.',
    notFoundOrDenied: '이 워크스페이스가 존재하지 않거나 접근 권한이 없습니다.',
    accessDenied: '이 워크스페이스에 접근할 권한이 없습니다.',
    notMember: '이 워크스페이스의 멤버가 아닙니다.',
    adminRequired: '워크스페이스 관리자만 이 작업을 할 수 있습니다.',
    slugUnavailable:
      '이미 사용 중인 워크스페이스 URL입니다. 다른 URL을 선택하세요.',
    slugImmutable: '워크스페이스 URL은 변경할 수 없습니다.',
    deletionInProgress: '이 워크스페이스를 삭제하는 중입니다.',
    workspaceRequired: '계속하려면 워크스페이스를 여세요.',
    otherWorkspace: '이 항목은 다른 워크스페이스에 속해 있습니다.',
    memberNotInWorkspace:
      '추가하는 사람은 모두 이 워크스페이스의 멤버여야 합니다.',
    userNotMember: '이 사용자는 워크스페이스 멤버가 아닙니다.',
  },
  'pt-BR': {
    notFound: 'Espaço de trabalho não encontrado.',
    notFoundOrDenied:
      'Este espaço de trabalho não existe ou você não tem acesso a ele.',
    accessDenied: 'Você não tem acesso a este espaço de trabalho.',
    notMember: 'Você não é membro deste espaço de trabalho.',
    adminRequired:
      'Somente administradores do espaço de trabalho podem fazer isso.',
    slugUnavailable:
      'Essa URL de espaço de trabalho já está em uso. Escolha outra.',
    slugImmutable: 'A URL do espaço de trabalho não pode ser alterada.',
    deletionInProgress: 'Este espaço de trabalho está sendo excluído.',
    workspaceRequired: 'Abra um espaço de trabalho para continuar.',
    otherWorkspace: 'Este item pertence a outro espaço de trabalho.',
    memberNotInWorkspace:
      'Todas as pessoas adicionadas precisam ser membros deste espaço de trabalho.',
    userNotMember: 'Este usuário não é membro do espaço de trabalho.',
  },
  ru: {
    notFound: 'Рабочее пространство не найдено.',
    notFoundOrDenied:
      'Это рабочее пространство не существует, или у вас нет к нему доступа.',
    accessDenied: 'У вас нет доступа к этому рабочему пространству.',
    notMember: 'Вы не участник этого рабочего пространства.',
    adminRequired:
      'Это могут делать только администраторы рабочего пространства.',
    slugUnavailable:
      'Этот URL рабочего пространства уже занят. Выберите другой.',
    slugImmutable: 'URL рабочего пространства нельзя изменить.',
    deletionInProgress: 'Это рабочее пространство удаляется.',
    workspaceRequired: 'Откройте рабочее пространство, чтобы продолжить.',
    otherWorkspace: 'Этот элемент принадлежит другому рабочему пространству.',
    memberNotInWorkspace:
      'Все, кого вы добавляете, должны быть участниками этого рабочего пространства.',
    userNotMember: 'Этот пользователь не участник рабочего пространства.',
  },
  vi: {
    notFound: 'Không tìm thấy không gian làm việc.',
    notFoundOrDenied:
      'Không gian làm việc này không tồn tại hoặc bạn không có quyền truy cập.',
    accessDenied: 'Bạn không có quyền truy cập không gian làm việc này.',
    notMember: 'Bạn không phải là thành viên của không gian làm việc này.',
    adminRequired:
      'Chỉ quản trị viên không gian làm việc mới có thể thực hiện thao tác này.',
    slugUnavailable:
      'URL không gian làm việc này đã được sử dụng. Vui lòng chọn URL khác.',
    slugImmutable: 'Không thể thay đổi URL của không gian làm việc.',
    deletionInProgress: 'Không gian làm việc này đang được xóa.',
    workspaceRequired: 'Hãy mở một không gian làm việc để tiếp tục.',
    otherWorkspace: 'Mục này thuộc về một không gian làm việc khác.',
    memberNotInWorkspace:
      'Mọi người bạn thêm phải là thành viên của không gian làm việc này.',
    userNotMember:
      'Người dùng này không phải là thành viên của không gian làm việc.',
  },
  zh: {
    notFound: '未找到工作区。',
    notFoundOrDenied: '该工作区不存在，或你无权访问。',
    accessDenied: '你无权访问此工作区。',
    notMember: '你不是此工作区的成员。',
    adminRequired: '只有工作区管理员可以执行此操作。',
    slugUnavailable: '该工作区网址已被占用，请换一个。',
    slugImmutable: '工作区网址无法更改。',
    deletionInProgress: '此工作区正在删除中。',
    workspaceRequired: '请先打开一个工作区再继续。',
    otherWorkspace: '此项目属于其他工作区。',
    memberNotInWorkspace: '你添加的所有人都必须是此工作区的成员。',
    userNotMember: '该用户不是此工作区的成员。',
  },
  'zh-TW': {
    notFound: '找不到工作區。',
    notFoundOrDenied: '此工作區不存在，或你沒有存取權限。',
    accessDenied: '你沒有此工作區的存取權限。',
    notMember: '你不是此工作區的成員。',
    adminRequired: '只有工作區管理員可以執行此操作。',
    slugUnavailable: '此工作區網址已被使用，請改選其他網址。',
    slugImmutable: '工作區網址無法變更。',
    deletionInProgress: '此工作區正在刪除中。',
    workspaceRequired: '請先開啟一個工作區再繼續。',
    otherWorkspace: '此項目屬於其他工作區。',
    memberNotInWorkspace: '你新增的所有人都必須是此工作區的成員。',
    userNotMember: '此使用者不是此工作區的成員。',
  },
};

export function getWorkspaceErrorCopy(
  id: WorkspaceErrorId,
  locale: string | null | undefined
): string {
  return COPY[normalizeLocale(locale)][id];
}
